import { Button, Checkbox, FormControl, FormLabel, HStack, Input, Text, Textarea, VStack } from '@chakra-ui/react'
import { useState } from 'react'
import { EmailRichComposer } from '@/components/email/EmailRichComposer'
import { isEmptyHtml } from '@/lib/utils/emailAttachments'
import { invalidRecipientMessage } from '@/lib/utils/emailSafety'

type Props = {
  lockedSubject: string
  disabled?: boolean
  canReply?: boolean
  allowReplyAll?: boolean
  onReply: (body: string, cc: string, replyAll: boolean, attachments: File[]) => Promise<void>
  onNote: (body: string) => Promise<void>
}

export function ThreadComposer({ lockedSubject, disabled, canReply = true, allowReplyAll = true, onReply, onNote }: Props) {
  const [body, setBody] = useState('')
  const [noteBody, setNoteBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [cc, setCc] = useState('')
  const [replyAll, setReplyAll] = useState(false)
  const [sending, setSending] = useState(false)
  const [ccError, setCcError] = useState<string | null>(null)
  const [mode, setMode] = useState<'reply' | 'note'>(canReply ? 'reply' : 'note')

  const sendReply = async () => {
    if (isEmptyHtml(body)) return
    const problem = invalidRecipientMessage(cc)
    if (problem) {
      setCcError(problem)
      return
    }
    setCcError(null)
    setSending(true)
    try {
      await onReply(body, cc, replyAll, attachments)
      setBody('')
      setAttachments([])
    } finally {
      setSending(false)
    }
  }

  const sendNote = async () => {
    if (!noteBody.trim()) return
    setSending(true)
    try {
      await onNote(noteBody.trim())
      setNoteBody('')
    } finally {
      setSending(false)
    }
  }

  return (
    <VStack align="stretch" spacing={2}>
      <FormControl>
        <FormLabel fontSize="xs">Subject (locked to this request)</FormLabel>
        <Input size="sm" value={lockedSubject} isReadOnly />
      </FormControl>

      {canReply ? (
        <HStack spacing={2}>
          <Button size="xs" variant={mode === 'reply' ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setMode('reply')}>
            Email reply
          </Button>
          <Button size="xs" variant={mode === 'note' ? 'solid' : 'outline'} onClick={() => setMode('note')}>
            Internal note
          </Button>
        </HStack>
      ) : null}

      {mode === 'reply' && canReply ? (
        <>
          <EmailRichComposer
            value={body}
            onChange={setBody}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            placeholder="Write your reply…"
            minHeight="140px"
            isDisabled={disabled || sending}
          />
          <FormControl>
            <FormLabel fontSize="xs">Cc</FormLabel>
            <Input
              size="sm"
              value={cc}
              onChange={(event) => {
                setCc(event.target.value)
                setCcError(null)
              }}
              placeholder="name@client.com"
            />
            {ccError ? (
              <Text fontSize="xs" color="red.500">
                {ccError}
              </Text>
            ) : null}
          </FormControl>
          {allowReplyAll ? (
            <Checkbox size="sm" isChecked={replyAll} onChange={(event) => setReplyAll(event.target.checked)}>
              Reply all (uses saved To and Cc)
            </Checkbox>
          ) : null}
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={disabled || isEmptyHtml(body)}
            isLoading={sending}
            onClick={() => void sendReply()}
          >
            Reply
          </Button>
        </>
      ) : (
        <>
          <Textarea
            size="sm"
            placeholder="Write an internal note"
            value={noteBody}
            onChange={(event) => setNoteBody(event.target.value)}
          />
          <Button
            size="sm"
            variant="outline"
            isDisabled={disabled || !noteBody.trim()}
            isLoading={sending}
            onClick={() => void sendNote()}
          >
            Internal note
          </Button>
        </>
      )}
    </VStack>
  )
}
